<template>
  <div
    class="oc-page-controls mb-25"
    :title="pageHint || undefined"
  >
    <div class="oc-page-controls__bar">
      <div class="oc-page-controls__item">
        <span class="oc-page-controls__lbl">Starts</span>
        <b-form-select
          :id="`oc-page-start-${sectionKey}`"
          size="sm"
          class="oc-page-controls__ctl oc-page-controls__ctl--start"
          :value="pageStartMode"
          :disabled="disabled"
          :options="pageStartSelectOptions"
          @change="$emit('page-start-change', $event)"
        />
      </div>
      <div class="oc-page-controls__item">
        <span
          class="oc-page-controls__lbl"
          title="Default body size — bold/size inline in editor"
        >Body</span>
        <b-form-select
          :id="`oc-font-${sectionKey}`"
          size="sm"
          class="oc-page-controls__ctl oc-page-controls__ctl--xs"
          :value="fontSizePx"
          :disabled="disabled"
          :options="fontSizeSelectOptions"
          @change="$emit('font-size-change', Number($event))"
        />
      </div>

      <template v-if="showSectionTitlePanel">
        <span class="oc-page-controls__sep" aria-hidden="true" />
        <div class="oc-page-controls__item oc-page-controls__item--grow">
          <span class="oc-page-controls__lbl">Title</span>
          <b-form-input
            :id="`oc-section-title-${sectionKey}`"
            size="sm"
            class="oc-page-controls__ctl"
            :value="sectionTitleText"
            :disabled="disabled"
            :placeholder="defaultSectionTitle"
            :style="sectionTitleInputStyle"
            @input="$emit('section-title-change', $event)"
          />
        </div>
        <div class="oc-page-controls__item">
          <span class="oc-page-controls__lbl">Sz</span>
          <b-form-select
            :id="`oc-section-title-size-${sectionKey}`"
            size="sm"
            class="oc-page-controls__ctl oc-page-controls__ctl--xs"
            :value="sectionTitleFontSizePx"
            :disabled="disabled"
            :options="fontSizeSelectOptions"
            @change="$emit('section-title-font-size-change', Number($event))"
          />
        </div>
        <div class="oc-page-controls__item">
          <span class="oc-page-controls__lbl">Wt</span>
          <b-form-select
            :id="`oc-section-title-weight-${sectionKey}`"
            size="sm"
            class="oc-page-controls__ctl oc-page-controls__ctl--xs"
            :value="sectionTitleFontWeight"
            :disabled="disabled"
            :options="fontWeightSelectOptions"
            @change="$emit('section-title-font-weight-change', $event)"
          />
        </div>
        <div class="oc-page-controls__item">
          <span class="oc-page-controls__lbl">Al</span>
          <b-form-select
            :id="`oc-section-title-align-${sectionKey}`"
            size="sm"
            class="oc-page-controls__ctl oc-page-controls__ctl--xs"
            :value="sectionTitleAlign"
            :disabled="disabled"
            :options="shortTitleAlignOptions"
            @change="$emit('section-title-align-change', $event)"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  BFormInput,
  BFormSelect,
} from 'bootstrap-vue'
import {
  OFFER_FONT_SIZE_OPTIONS,
  OFFER_PAGE_START_MODE,
  OFFER_PAGE_START_MODE_OPTIONS,
  OFFER_TITLE_ALIGN_OPTIONS,
} from '@/utils/offer-template-editor'

const COMPACT_PAGE_START_LABELS = {
  continue: 'Same page',
  newPage: 'New page',
  newPageWithTitle: 'New page + title',
}

export default {
  name: 'OfferSectionPageControls',
  components: {
    BFormInput,
    BFormSelect,
  },
  props: {
    sectionKey: {
      type: String,
      required: true,
    },
    pageStartMode: {
      type: String,
      default: OFFER_PAGE_START_MODE.CONTINUE,
    },
    fontSizePx: {
      type: Number,
      default: 10,
    },
    sectionTitleText: {
      type: String,
      default: '',
    },
    sectionTitleAlign: {
      type: String,
      default: 'center',
    },
    sectionTitleFontSizePx: {
      type: Number,
      default: 11,
    },
    sectionTitleFontWeight: {
      type: String,
      default: 'bold',
    },
    defaultSectionTitle: {
      type: String,
      default: '',
    },
    supportsTitle: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    pageHint: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      newPageWithTitle: OFFER_PAGE_START_MODE.NEW_PAGE_WITH_TITLE,
    }
  },
  computed: {
    pageStartSelectOptions() {
      const options = this.supportsTitle
        ? OFFER_PAGE_START_MODE_OPTIONS
        : OFFER_PAGE_START_MODE_OPTIONS.filter(
          opt => opt.value !== OFFER_PAGE_START_MODE.NEW_PAGE_WITH_TITLE,
        )
      return options.map(opt => ({
        value: opt.value,
        text: COMPACT_PAGE_START_LABELS[opt.value] || opt.text,
      }))
    },
    fontSizeSelectOptions() {
      return OFFER_FONT_SIZE_OPTIONS.map(size => ({
        value: size,
        text: `${size}`,
      }))
    },
    fontWeightSelectOptions() {
      return [
        { value: 'normal', text: 'N' },
        { value: 'bold', text: 'B' },
      ]
    },
    shortTitleAlignOptions() {
      return OFFER_TITLE_ALIGN_OPTIONS.map(opt => ({
        value: opt.value,
        text: opt.value === 'left' ? 'L' : opt.value === 'right' ? 'R' : 'C',
      }))
    },
    showSectionTitlePanel() {
      return this.supportsTitle && this.pageStartMode === this.newPageWithTitle
    },
    sectionTitleInputStyle() {
      const align = this.sectionTitleAlign || 'center'
      return { textAlign: align }
    },
  },
}
</script>

<style scoped>
.oc-page-controls {
  background: #eef0f3;
  border-radius: 0.25rem;
  padding: 0.25rem 0.35rem;
}

.oc-page-controls__bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.2rem 0.35rem;
}

.oc-page-controls__item {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  flex: 0 0 auto;
}

.oc-page-controls__item--grow {
  flex: 1 1 6rem;
  min-width: 5.5rem;
}

.oc-page-controls__lbl {
  font-size: 0.625rem;
  font-weight: 600;
  color: #6e6b7b;
  white-space: nowrap;
  line-height: 1;
}

.oc-page-controls__sep {
  width: 1px;
  height: 1.25rem;
  background: rgba(0, 0, 0, 0.1);
  margin: 0 0.1rem;
  flex-shrink: 0;
}

.oc-page-controls__ctl--start {
  width: 7.25rem;
  min-width: 7rem;
}

.oc-page-controls__ctl--xs {
  width: 2.85rem;
  min-width: 2.85rem;
  padding-left: 0.25rem;
  padding-right: 1.35rem;
}

.oc-page-controls >>> .custom-select-sm,
.oc-page-controls >>> .form-control-sm {
  font-size: 0.6875rem;
  padding-top: 0.1rem;
  padding-bottom: 0.1rem;
  height: calc(1.4em + 0.25rem + 2px);
  line-height: 1.2;
}
</style>
